import React from 'react';
import { Button, Grid, LinearProgress } from '@mui/material';
import Alert from '@mui/lab/Alert';
import FieldsOrder from '../fields/FieldsOrder';
import MarkDoc from './doc/MarkDoc';
import MarkForm from './form/MarkForm';
import MarkNav from './MarkNav';
import useGroundTruths from '../hooks/useGroundTruths';
import useGetAllFields from '../hooks/useGetAllFields';
import { useFullscreenBodyScroll } from '../hooks/useFullscreenBodyScroll';
import { FIELDS_PAGE_SIZE } from '../../constants';
import { IDocument, IField, IGroundTruth, ITrainingSet } from '../../types/my-types';
import { MarkStateContext, MarkDispatchContext } from '../../context/MarkContext';
import { getFieldDataTypeAndConfig } from '../fields/utils/utilsFields';
import { useAtom } from 'jotai';
import { markFullscreenAtom } from '../../atoms/mark-atoms';
import {
  ToolContainer,
  NavSection,
  ContentSection,
  LeftPanel,
  RightPanel,
} from '../tool/layout/ToolLayout.styles';

interface MarkProps {
  docIndex: number;
  trainingSet: ITrainingSet;
  docs: IDocument[];
}

const Mark: React.FC<MarkProps> = ({ docIndex, trainingSet, docs }) => {
  const [on, setOn] = React.useState(false);
  const [limit, setLimit] = React.useState(FIELDS_PAGE_SIZE);
  const [isFullscreen, setIsFullscreen] = useAtom(markFullscreenAtom);

  useFullscreenBodyScroll(isFullscreen);

  const { misMatchedIndexTokens } = React.useContext(MarkStateContext);

  const dispatch = React.useContext<React.Dispatch<any>>(MarkDispatchContext);

  const doc = docs[docIndex % 100];
  // console.log({ doc });
  // rerender doesn't trigger query refetch
  // but change query vars does
  const { data, loading, isFetching, total } = useGetAllFields(trainingSet.id, limit);
  const groundTruths: IGroundTruth[] = useGroundTruths(doc?.id, trainingSet.id);

  const fields: IField[] = React.useMemo(
    () => data?.pages.flatMap((page) => page.results) ?? [],
    [data]
  );

  const docTotalCount: number = trainingSet.documents.length;

  React.useEffect(() => {
    dispatch({ type: 'SET_MISMATCHED_INDEX_TOKENS', payload: [] });
  }, [docIndex, dispatch]);

  // console.log('MarkContextProvider', MarkContextProvider);
  // TODO: the left grid width is changing on reorder button clicked, use fixed ratio 3:9

  if (loading || !doc) {
    return <LinearProgress />;
  }

  return (
    <ToolContainer isFullscreen={isFullscreen}>
      <NavSection container spacing={1} className="flex-no-shrink">
        <Grid item xs={12}>
          <MarkNav
            steps={docTotalCount}
            docIndex={docIndex}
            documents={docs}
            tokenizerVersion={trainingSet.tokenizer_version}
            batchLabel={trainingSet.label}
            trainingSetId={trainingSet.id}
            isFullscreen={isFullscreen}
            setIsFullscreen={setIsFullscreen}
          />
        </Grid>
      </NavSection>
      {misMatchedIndexTokens.length > 0 ? (
        <Grid>
          <div style={{ maxWidth: '98%', margin: '0 auto', paddingBottom: '20px' }}>
            <Alert variant="outlined" severity="error" data-cy="alert-mismatched-tokens">
              Some marked fields no longer match the current document tokens. The fields that need
              to be re-marked are highlighted in red in the field list below.
            </Alert>
          </div>
        </Grid>
      ) : null}
      <ContentSection
        container
        columnSpacing={1}
        rowSpacing={0}
        className="flex-section flex-flow-row"
        isFullscreen={isFullscreen}
      >
        <LeftPanel item xs={4} lg={3} xl={2} className="flex-col-scroll" data-cy="div-mark-form">
          <>
            <div>
              {on ? (
                <Button
                  id="mark-button"
                  variant="outlined"
                  color="primary"
                  data-cy="btn-mark-reorder"
                  onClick={() => {
                    // trigger refetch when hit mark button
                    if (on) {
                      limit === 100 ? setLimit(99) : setLimit(100);
                    }
                    setOn((on) => !on);
                  }}
                  sx={{ position: 'relative', top: '30px', left: '20px' }}
                >
                  {on ? 'Cancel' : 'Reorder'}
                </Button>
              ) : null}
            </div>
            <div style={{ marginTop: on ? '6em' : '0', height: '100%' }}>
              {on ? (
                <div style={{ marginLeft: '1em' }}>
                  <FieldsOrder
                    trainingSetId={trainingSet?.id}
                    getFieldDataTypeAndConfig={(fieldId) =>
                      getFieldDataTypeAndConfig(fields, fieldId)
                    }
                  />
                </div>
              ) : (
                <MarkForm
                  fields={fields}
                  trainingSet={trainingSet}
                  doc={doc}
                  groundTruths={groundTruths}
                  on={on}
                  limit={limit}
                  setLimit={setLimit}
                  setOn={setOn}
                />
              )}
            </div>
          </>
        </LeftPanel>
        <RightPanel
          item
          xs={8}
          lg={9}
          xl={10}
          className="flex-col-scroll"
          id="doc-page-wrapper"
          isFullscreen={isFullscreen}
        >
          <MarkDoc
            doc={doc}
            fields={fields}
            trainingSet={trainingSet}
            groundTruths={groundTruths}
          />
        </RightPanel>
      </ContentSection>
    </ToolContainer>
  );
};

export default Mark;
